/**
 * Espelho de `backend/src/services/confirmacao.ts`.
 *
 * O backend usa a regra para SOMAR (quanto está aguardando confirmação); o
 * front usa para DESENHAR (o ícone na linha da lista). São usos diferentes da
 * mesma regra, e por isso ela existe dos dois lados.
 *
 * O que impede de divergirem: `shared/confirmacao.casos.json`, lido pelas duas
 * suítes. Acrescentar caso lá obriga os dois a passarem — é a lição de
 * `monthView` e do formatador de mês, onde a segunda cópia divergiu em
 * silêncio até alguém ver na tela.
 *
 * O estado NÃO é uma coluna: a transação está confirmada no mês M se existe
 * check do lembrete ligado a ela em M. Uma coluna `paid` seria segunda fonte
 * da mesma verdade e divergiria na primeira vez que alguém desmarcasse.
 *
 * Desde 15/09 há duas fontes a mais, também por mês e também fora de
 * `transactions`: o valor do mês informado e a fatura paga do cartão.
 */

export const mesDe = (data: string | Date | null | undefined): string | null => {
  const s = String(data ?? '').slice(0, 7);
  return /^\d{4}-\d{2}$/.test(s) ? s : null;
};

export interface Vinculo {
  reminderId: string;
  transactionId: string;
  /** Mês em que o lembrete nasceu. Piso da cobrança. */
  desde: string;
  /** Último mês do lembrete, quando tem prazo. */
  ate?: string | null;
}

/**
 * A partir de que mês a confirmação v2 vale (15/09). Meses anteriores ficam
 * como eram: sem isto, cada mensal e cada compra de cartão de meses antigos
 * apareceria "aguardando" para sempre — ela nunca confirmou nada daquela
 * época, porque não havia onde.
 */
export const CONFIRMACAO_DESDE = '2026-09';

/** O que a regra precisa saber de um lançamento. `id` sozinho é o caminho antigo: só lembrete. */
export interface LinhaConfirmavel {
  id: string;
  isMonthly?: boolean;
  paymentMethod?: string | null;
  debitDirection?: string | null;
}

/** Valor do mês informado e fatura paga (15/09). Ausente, a regra é a antiga. */
export interface PagamentosConfirmados {
  /** `${transactionId}|${YYYY-MM}` */
  informados: Set<string>;
  /** `${paymentMethodId}|${YYYY-MM}` */
  faturasPagas: Set<string>;
  /** Métodos com dia de pagamento — cartão com fatura. */
  metodosComFatura: Set<string>;
}

export interface EstadoDeConfirmacao {
  vinculos: Vinculo[];
  /** `${reminderId}|${YYYY-MM}` do que já foi marcado. */
  checks: Set<string>;
  pagamentos?: PagamentosConfirmados;
}

export type SituacaoDaLinha = 'sem-vinculo' | 'aguardando' | 'confirmada';

export const situacaoDaLinha = (
  linha: string | LinhaConfirmavel,
  mes: string,
  e: EstadoDeConfirmacao,
): SituacaoDaLinha => {
  const l: LinhaConfirmavel = typeof linha === 'string' ? { id: linha } : linha;
  const lembrete = (): SituacaoDaLinha | null => {
    const ativos = e.vinculos.filter(x =>
      x.transactionId === l.id
      && !(x.desde && mes < x.desde)
      && !(x.ate && mes > x.ate));
    if (ativos.length === 0) return null;
    return ativos.some(v => e.checks.has(`${v.reminderId}|${mes}`)) ? 'confirmada' : 'aguardando';
  };

  // A v2 — o porquê de cada ramo está no backend.
  const p = e.pagamentos;
  if (p && mes >= CONFIRMACAO_DESDE && !l.debitDirection) {
    if (l.paymentMethod && p.metodosComFatura.has(l.paymentMethod)) {
      return p.faturasPagas.has(`${l.paymentMethod}|${mes}`) ? 'confirmada' : 'aguardando';
    }
    if (l.isMonthly) {
      if (p.informados.has(`${l.id}|${mes}`)) return 'confirmada';
      return lembrete() === 'confirmada' ? 'confirmada' : 'aguardando';
    }
  }
  return lembrete() ?? 'sem-vinculo';
};

export const lembreteAtivoEm = (
  l: { endDate?: string | null },
  mes: string,
): boolean => {
  if (!l.endDate) return true;
  const fim = mesDe(l.endDate);
  return fim === null ? true : mes <= fim;
};

/**
 * Monta o estado a partir do que a tela já carrega.
 *
 * Nenhum endpoint novo para o lembrete: `GET /api/reminders?month=` devolve
 * `transactionId`, `checked` e `createdAt`, e o `checked` vem do mês pedido,
 * então a chave é montada com ESSE mês.
 *
 * Os pagamentos (15/09) vêm das transações (`monthValues`) e dos métodos
 * (`billingDay`, `faturasPagas`). Sem eles, o estado é o antigo.
 */
export const estadoDeLembretes = (
  lembretes: { id: string; transactionId?: string | null; checked?: boolean; createdAt?: string; endDate?: string | null }[],
  mes: string,
  fontes?: {
    transactions: { id: string; monthValues?: Record<string, number> }[];
    paymentMethods: { id: string; billingDay?: number | null; faturasPagas?: string[] }[];
  },
): EstadoDeConfirmacao => {
  const vinculos: Vinculo[] = [];
  const checks = new Set<string>();
  for (const l of lembretes) {
    if (!l.transactionId) continue;
    vinculos.push({
      reminderId: l.id,
      transactionId: l.transactionId,
      desde: mesDe(l.createdAt) ?? '0000-00',
      ate: mesDe(l.endDate),
    });
    if (l.checked) checks.add(`${l.id}|${mes}`);
  }
  if (!fontes) return { vinculos, checks };

  const informados = new Set<string>();
  for (const t of fontes.transactions) {
    for (const m of Object.keys(t.monthValues ?? {})) informados.add(`${t.id}|${m}`);
  }
  const faturasPagas = new Set<string>();
  const metodosComFatura = new Set<string>();
  for (const pm of fontes.paymentMethods) {
    if (pm.billingDay) metodosComFatura.add(pm.id);
    for (const m of pm.faturasPagas ?? []) faturasPagas.add(`${pm.id}|${m}`);
  }
  return { vinculos, checks, pagamentos: { informados, faturasPagas, metodosComFatura } };
};
